/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { Radio, RadioGroup, FormControlLabel, FormControl, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import WorkOrdersApiService from '../../../../Jobs/Services/WorkOrdersService';
import { UpdateTaskScheduleInput, WorkOrderYesNoType } from '../../../../../API';
import { IAdjustProofModalProps } from '../AllSchedules.Model';
import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import DeletePopup from '../../../../../Shared/Components/Common/DeletePopup/DeletePopup';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { ProofActionTypes, ProofSelectionTypes, refreshDelay } from '../../../../../Shared/Constants/App';

import { Icons } from '../../../../../Shared/Constants/Icons';

import { buttonStyles } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { ModifyButtonContainer, ModifyScheduleText } from '../AllJobs.Style';
import { CloseIconStyle } from '../../../../../Shared/Components/Common/SuccessMessagePopup/SuccessMessagePopup.Style';
import { DialogWrapper } from '../../CreateBid.Style';

type ProofSelectionType = (typeof ProofSelectionTypes)[keyof typeof ProofSelectionTypes] | null;
type ProofActionType = (typeof ProofSelectionTypes)[keyof typeof ProofSelectionTypes] | null;

const ProofSettingOptionsPopup = ({
  open,
  onClose,
  selectedJobs,
  setRefreshKey,
}: IAdjustProofModalProps): JSX.Element => {
  const [selectionType, setSelectionType] = useState<ProofSelectionType>(null);
  const [proofAction, setProofAction] = useState<ProofActionType>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [servicesData, setServicesData] = useState<any>(null);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { t } = useTranslation(['jobManagement']);
  const navigate = useNavigate();
  const { radioButton, radioButtonText } = buttonStyles;
  const handleSelectionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectionType(event.target.value as ProofSelectionType);
    setProofAction(null);
  };

  const handleProofActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProofAction(event.target.value as ProofActionType);
  };

  const fetchScheduleMasterServices = async () => {
    if (selectedJobs.length === 0) return;

    setIsLoading(true);
    setIsNextLoading(true);
    try {
      const woScheduleMasterIds = selectedJobs
        .map((job) => job.woScheduleMasterId)
        .filter(Boolean)
        .join(',');
      const response = await AllJobsApiService.getMasterServices(woScheduleMasterIds);
      setServicesData(response.data);
      return response.data;
    } catch (error) {
      console.error(t('jobManagement:errorFetchingServices'), error);
      throw error;
    } finally {
      setIsLoading(false);
      setIsNextLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchScheduleMasterServices();
    };
    if (open) {
      loadData();
    }
  }, [open, selectedJobs]);

  const handleNext = () => {
    if (selectionType === ProofSelectionTypes.Certain) {
      navigate('/all-schedules/modify-proof-settings/specific-tasks', {
        state: {
          selectedJobs,
          servicesData,
        },
      });
      onClose();
    }
  };

  const handleSubmit = () => {
    if (selectionType === ProofSelectionTypes.All && proofAction && servicesData) {
      setIsDeletePopupVisible(true);
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleConfirmSubmit = async () => {
    try {
      setIsLoading(true);
      const updatePayload: UpdateTaskScheduleInput[] = [];

      servicesData.forEach((service: any) => {
        if (service.tasks && Array.isArray(service.tasks)) {
          service.tasks.forEach((task: any) => {
            if (task.woScheduleServiceId) {
              updatePayload.push({
                taskScheduleId: task.woScheduleServiceId,
                isProofOfCompletionRequired:
                  proofAction === ProofActionTypes.Required ? WorkOrderYesNoType.Yes : WorkOrderYesNoType.No,
              });
            }
          });
        }
      });
      if (updatePayload.length > 0) {
        const result = await WorkOrdersApiService.updateTaskSchedule(updatePayload);
        if (result.errors && result.errors.length > 0) {
          setSnackbarMessage(result.errors);
          setSnackbarOpen(true);
          setSnackbarSeverity('error');
        } else {
          setSnackbarMessage(`${t('jobManagement:updatedTaskProofSuccess')}`);
          setSnackbarOpen(true);
          setSnackbarSeverity('success');
          setTimeout(() => {
            onClose();
          }, refreshDelay);
        }
      }
    } catch (error) {
      setSnackbarMessage(t('jobManagement:updatedTaskProofFail'));
      setSnackbarOpen(true);
      setSnackbarSeverity('error');
    } finally {
      setIsLoading(false);
      setIsDeletePopupVisible(false);
      setTimeout(() => {
        setRefreshKey?.((prev) => prev + 1);
      }, refreshDelay);
    }
  };

  return (
    <DialogWrapper open={open} onClose={onClose}>
      <CloseIconStyle onClick={onClose}>
        <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
      </CloseIconStyle>
      <FormControl component="fieldset" sx={{ width: '100%', mt: 2 }}>
        <ModifyScheduleText sx={{ marginBottom: '1.5rem' }}>
          {t('jobManagement:proofSettingOptions')}
        </ModifyScheduleText>
        <RadioGroup value={selectionType || ''} onChange={handleSelectionTypeChange}>
          <FormControlLabel
            value="all"
            control={
              <Radio
                sx={{
                  ...radioButton,
                }}
              />
            }
            label={t('jobManagement:manageProofForAllTasks')}
            sx={{
              ...radioButtonText,
              marginBottom: '1rem',
            }}
          />
          <Collapse in={selectionType === ProofSelectionTypes.All}>
            <FormControl component="fieldset" sx={{ width: '100%', pl: 3 }}>
              <RadioGroup value={proofAction || ''} onChange={handleProofActionChange}>
                <FormControlLabel
                  value="required"
                  control={
                    <Radio
                      sx={{
                        ...radioButton,
                      }}
                    />
                  }
                  label={t('jobManagement:changeAllTasksToProofRequired')}
                  sx={{
                    ...radioButtonText,
                    marginBottom: '1rem',
                  }}
                />
                <FormControlLabel
                  value="optional"
                  control={
                    <Radio
                      sx={{
                        ...radioButton,
                      }}
                    />
                  }
                  label={t('jobManagement:changeAllTasksToProofOptional')}
                  sx={{
                    ...radioButtonText,
                    marginBottom: '1rem',
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Collapse>
          <FormControlLabel
            value="certain"
            control={
              <Radio
                sx={{
                  ...radioButton,
                }}
              />
            }
            label={t('jobManagement:manageProofForCertainTasks')}
            sx={{
              ...radioButtonText,
              marginBottom: '1rem',
            }}
          />
        </RadioGroup>
      </FormControl>

      <ModifyButtonContainer sx={{ gap: '1rem', marginTop: '1rem' }}>
        <Button type={ButtonType.Secondary} onClick={onClose} label={t('assignJob:cancel')} />
        <Button
          type={ButtonType.Primary}
          onClick={selectionType === ProofSelectionTypes.All && proofAction ? handleSubmit : handleNext}
          disabled={!selectionType || (selectionType === ProofSelectionTypes.All && !proofAction) || isLoading}
          label={selectionType === ProofSelectionTypes.All && proofAction ? t('dashboard:submit') : t('assignJob:next')}
          isLoading={isNextLoading}
        />
      </ModifyButtonContainer>
      <DeletePopup
        open={isDeletePopupVisible}
        onClose={() => setIsDeletePopupVisible(false)}
        message={`${t('jobManagement:confirmTaskChange')} "${proofAction === ProofActionTypes.Required ? t('jobManagement:proofRequired') : t('jobManagement:proofOptional')}"?`}
        primaryButtonLabel={t('jobManagement:yes')}
        name={''}
        isPrimaryButtonLoading={isLoading}
        primaryAction={handleConfirmSubmit}
        secondaryButtonLabel={t('jobManagement:no')}
        secondaryAction={() => setIsDeletePopupVisible(false)}
      />
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </DialogWrapper>
  );
};

export default ProofSettingOptionsPopup;
